import { AgentChatMessage, AgentRun } from './AgentChat.types';

const TOOL_LINE = /^([•✓✗])\s+(.+)$/;

export type ThreadItem =
  | { type: 'message'; message: AgentChatMessage }
  | {
      type: 'activity';
      id: string;
      text: string;
      failed: boolean;
      pending: boolean;
    };

const joinAnd = (parts: string[]): string => {
  if (parts.length <= 1) return parts[0] ?? '';
  if (parts.length === 2) return `${parts[0]} and ${parts[1]}`;
  return `${parts.slice(0, -1).join(', ')}, and ${parts[parts.length - 1]}`;
};

type Bucket = 'crates' | 'library' | 'serato' | 'vdj' | 'streaming' | 'other';

const bucketFor = (label: string): Bucket => {
  const line = label.toLowerCase();
  if (line.includes('serato')) return 'serato';
  if (line.includes('virtualdj') || line.includes('virtual dj')) return 'vdj';
  if (
    line.includes('stream') ||
    line.includes('spotify') ||
    line.includes('tidal') ||
    line.includes('apple')
  ) {
    return 'streaming';
  }
  if (line.includes('crate hackers') || line.includes('crate')) return 'crates';
  if (
    line.includes('library') ||
    line.includes('folder') ||
    line.includes('duplicate') ||
    line.includes('health') ||
    line.includes('recycle')
  ) {
    return 'library';
  }
  return 'other';
};

export const activitySentence = (labels: string[]): string => {
  const buckets = new Set(labels.map(bucketFor));
  const main: string[] = [];
  if (buckets.has('crates')) main.push('Crate Hackers crates');
  if (buckets.has('library')) main.push('your library');
  const extra: string[] = [];
  if (buckets.has('serato')) extra.push('Serato');
  if (buckets.has('vdj')) extra.push('VirtualDJ');
  if (buckets.has('streaming')) extra.push('streaming');

  if (main.length && extra.length) {
    return `Searching ${joinAnd(main)}. Also checking ${joinAnd(extra)}.`;
  }
  if (main.length) return `Searching ${joinAnd(main)}.`;
  if (extra.length) return `Checking ${joinAnd(extra)}.`;
  if (labels[0]) return labels[0];
  return 'Searching.';
};

export const isNoisyAgentText = (text: string): boolean => {
  if (/^\|.+\|/m.test(text)) return false;
  // Extracted song lists are the answer, however long they run.
  if (text.includes('```songlist')) return false;
  const lower = text.toLowerCase();
  if (
    /\b(mcp|list_tracks|get_crate|input schema|crate id|ch:\/\/track)\b/.test(
      lower,
    )
  ) {
    return true;
  }
  if (
    /\b(i'll |i will |fixing the |refining |digging into |good data found|the response will|running parallel)\b/.test(
      lower,
    )
  ) {
    return true;
  }
  return text.length > 400 && !/\bhere are\b/i.test(text);
};

const isHeartbeat = (text: string): boolean => /^still working/i.test(text);

type Wave = {
  id: string;
  labels: string[];
  failed: boolean;
  pending: boolean;
};

const toActivity = (wave: Wave): ThreadItem => ({
  type: 'activity',
  id: wave.id,
  text: activitySentence(wave.labels),
  failed: wave.failed,
  pending: wave.pending,
});

/** Hide thinking, collapse tool rows into one status line, drop process chatter. */
export const presentThread = (
  messages: AgentChatMessage[],
  run: Pick<AgentRun, 'status'>,
): ThreadItem[] => {
  const running = run.status === 'queued' || run.status === 'running';
  const lastAgent = [...messages]
    .reverse()
    .find((entry) => entry.role === 'agent');

  const items: ThreadItem[] = [];
  let wave: Wave | null = null;
  let pending = new Set<string>();

  const flush = () => {
    if (!wave) return;
    if (running || wave.failed) items.push(toActivity(wave));
    wave = null;
    pending = new Set();
  };

  for (const entry of messages) {
    if (entry.role === 'thinking') continue;
    if (entry.role === 'tool') {
      if (isHeartbeat(entry.content)) continue;
      const next = entry.content.match(TOOL_LINE);
      if (!next) continue;
      const [, mark, label] = next;
      if (!wave) {
        wave = {
          id: entry.id,
          labels: [],
          failed: false,
          pending: true,
        };
      }
      if (!wave.labels.includes(label)) wave.labels.push(label);
      if (mark === '•') pending.add(label);
      if (mark === '✓' || mark === '✗') pending.delete(label);
      if (mark === '✗') wave.failed = true;
      wave.pending = pending.size > 0;
      continue;
    }
    flush();
    // Never hide the last agent message: while streaming it is the answer
    // growing in place, and hiding it mid-stream makes the reply flash away.
    if (
      entry.role === 'agent' &&
      isNoisyAgentText(entry.content) &&
      entry.id !== lastAgent?.id
    ) {
      continue;
    }
    items.push({ type: 'message', message: entry });
  }
  flush();
  return items;
};
